import React from 'react';
import { Box, Stack, Typography } from '@mui/material';

function formatDateTime(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

// EXIF ExposureTime is a plain seconds value (e.g. 0.001) - shown as a
// fraction ("1/1000s") since that's how photographers actually read it.
function formatExposureTime(seconds) {
  if (typeof seconds !== 'number' || !seconds) return null;
  if (seconds >= 1) return `${seconds}s`;
  const denominator = Math.round(1 / seconds);
  return `1/${denominator}s`;
}

// One label/value row per available field - a photo with sparse metadata
// (no EXIF, organiser upload with no submitter) just renders fewer rows
// rather than empty placeholders, so the panel never looks broken/incomplete.
function buildRows(photo) {
  if (!photo) return [];
  const exif = photo.exif || {};
  const filename = photo.path || photo.filename || photo.original_filename || photo.uc_filename || null;
  const camera = [exif.Make, exif.Model].filter(Boolean).join(' ') || null;
  const settings = [
    exif.FNumber ? `f/${exif.FNumber}` : null,
    formatExposureTime(exif.ExposureTime),
    exif.ISO ? `ISO ${exif.ISO}` : null,
    exif.FocalLength ? `${exif.FocalLength}mm` : null,
  ].filter(Boolean).join(' · ') || null;
  const hasGps = typeof exif.GPSLatitude === 'number' && typeof exif.GPSLongitude === 'number';
  const submittedBy = photo.submitted_by_name
    ? (photo.submitted_by_username ? `${photo.submitted_by_name} (@${photo.submitted_by_username})` : photo.submitted_by_name)
    : null;

  return [
    ['File', filename],
    ['Taken', formatDateTime(photo.captured_at)],
    ['Submitted', formatDateTime(photo.created_at)],
    ['Camera', camera],
    ['Lens', exif.LensModel || null],
    ['Settings', settings],
    ['Location', hasGps ? `${exif.GPSLatitude.toFixed(5)}, ${exif.GPSLongitude.toFixed(5)}` : null],
    ['Submitted by', submittedBy],
  ].filter(([, value]) => Boolean(value));
}

// Shown/hidden by a small info-icon toggle in every photo previewer (see
// EventPage.js's main gallery dialog, SubmissionPreviewDialog, MyPhotosSection,
// and Moments.js's PhotoDialog). Deliberately its own component rather than
// inlined in each dialog - one place to add a new field, one visual language
// for "photo info" across the whole app. Renders nothing when the photo truly
// has no extra info to show (organiser's own upload, no EXIF) rather than an
// empty box, so toggling it open never looks like something failed to load.
export default function PhotoInfoPanel({ photo, dark = false, sx }) {
  const rows = buildRows(photo);
  const labelColor = dark ? 'rgba(255,255,255,0.6)' : 'text.secondary';
  const valueColor = dark ? 'common.white' : 'text.primary';

  if (!rows.length) {
    return (
      <Box sx={{ px: 2, py: 1.25, ...sx }}>
        <Typography variant="caption" sx={{ color: labelColor }}>
          No additional photo info available.
        </Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={0.6} sx={{ px: 2, py: 1.25, ...sx }}>
      {rows.map(([label, value]) => (
        <Stack key={label} direction="row" spacing={1.5} sx={{ minWidth: 0 }}>
          <Typography variant="caption" sx={{ color: labelColor, width: 82, flexShrink: 0 }}>
            {label}
          </Typography>
          <Typography variant="caption" sx={{ color: valueColor, wordBreak: 'break-word', minWidth: 0 }}>
            {value}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
}
